import axios from 'axios';

export const getReports = async (params = {}) => {
  const { data } = await axios.get('/api/reports', { params });
  return data;
};

export const getReport = async (reportId) => {
  if (!reportId) {
    throw new Error('reportId is required');
  }
  const { data } = await axios.get(`/api/reports/${reportId}`);
  return data;
};

export const updateReport = async ({ reportId, metadata, definition, changeNote, ifMatchVersion } = {}) => {
  if (!reportId) {
    throw new Error('reportId is required');
  }
  if (!changeNote) {
    throw new Error('changeNote is required');
  }
  const payload = {
    metadata,
    definition,
    changeNote,
  };
  if (ifMatchVersion) {
    payload.ifMatchVersion = ifMatchVersion;
  }
  const { data } = await axios.patch(`/api/reports/${reportId}`, payload);
  return data;
};

export const archiveReport = async (reportId) => {
  if (!reportId) {
    throw new Error('reportId is required');
  }
  const { data } = await axios.post(`/api/reports/${reportId}/archive`);
  return data;
};

export const unarchiveReport = async (reportId) => {
  if (!reportId) {
    throw new Error('reportId is required');
  }
  const { data } = await axios.post(`/api/reports/${reportId}/unarchive`);
  return data;
};

export const deleteReport = async (reportId) => {
  if (!reportId) {
    throw new Error('reportId is required');
  }
  const { data } = await axios.delete(`/api/reports/${reportId}`);
  return data;
};

export const getReportDatasets = async () => {
  const { data } = await axios.get('/api/reports/datasets');
  return data;
};

export const getReportMetrics = async () => {
  const { data } = await axios.get('/api/reports/metrics');
  return data;
};

export const getReportExtensions = async () => {
  const { data } = await axios.get('/api/reports/extensions');
  return data;
};

export const getReportOpsConfig = async () => {
  const { data } = await axios.get('/api/reports/config');
  return data;
};

export const queryReport = async ({
  reportId,
  filters,
  sort,
  page,
  pageSize,
  includeMetrics,
  emailEvidenceCursor,
  emailEvidenceLimit,
} = {}) => {
  if (!reportId) {
    throw new Error('reportId is required');
  }
  const payload = {
    filters,
    sort,
    page,
    pageSize,
    includeMetrics,
    emailEvidenceCursor,
    emailEvidenceLimit,
  };
  const { data } = await axios.post(`/api/reports/${reportId}/query`, payload);
  return data;
};

export const downloadReportEmailNotificationEvidence = async ({
  reportId,
  filters,
} = {}) => {
  if (!reportId) throw new Error('reportId is required');
  const response = await axios.post(
    `/api/reports/${reportId}/email-notification-evidence/export`,
    { filters },
    { responseType: 'blob' }
  );
  return response;
};

export const exportReport = async ({ reportId, format = 'csv', filters } = {}) => {
  if (!reportId) {
    throw new Error('reportId is required');
  }
  const payload = { format, filters };
  const { data } = await axios.post(`/api/reports/${reportId}/export`, payload);
  return data;
};

export const getReportExportStatus = async (exportId) => {
  if (!exportId) {
    throw new Error('exportId is required');
  }
  const { data } = await axios.get(`/api/reports/exports/${exportId}/status`);
  return data;
};

export const refreshReport = async ({ reportId, reason, recompute } = {}) => {
  if (!reportId) {
    throw new Error('reportId is required');
  }
  const payload = { reason, recompute };
  const { data } = await axios.post(`/api/reports/${reportId}/refresh`, payload);
  return data;
};

export const getReportRefreshStatus = async (reportId) => {
  if (!reportId) {
    throw new Error('reportId is required');
  }
  const { data } = await axios.get(`/api/reports/${reportId}/refresh/status`);
  return data;
};

export const executeReportAction = async ({ reportId, actionType, action, row, rows } = {}) => {
  if (!reportId) {
    throw new Error('reportId is required');
  }
  const resolvedActionType = actionType || action?.type;
  if (!resolvedActionType) {
    throw new Error('actionType is required');
  }
  const payload = {
    actionType: resolvedActionType,
    action,
    row,
    rows: Array.isArray(rows) ? rows : undefined,
  };
  const { data } = await axios.post(`/api/reports/${reportId}/actions/execute`, payload);
  return data;
};
